<template>
  <div>
    <div class="d-flex gap-2 align-items-center mb-3">
      <CompanySearchDropdown
        :model-value="companyId"
        placeholder="Select company"
        :with-label="false"
        :with-description="false"
        @update:model-value="onCompanyIdUpdate"
        @select="handleCompanySelect"
      />
    </div>

    <div v-if="!companyId" class="text-muted">
      Select a company to view invoicing data.
    </div>

    <div v-else>
      <!-- Sub-tabs -->
      <ul class="nav nav-tabs mb-3">
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'bundles' }"
            @click="activeTab = 'bundles'"
          >
            Invoice Bundles
            <span v-if="bundles.length > 0" class="badge bg-secondary ms-1">{{
              bundles.length
            }}</span>
          </button>
        </li>
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'pending-fees' }"
            @click="activeTab = 'pending-fees'"
          >
            Pending Fee Items
            <span
              v-if="pendingFeeItems.length > 0"
              class="badge bg-secondary ms-1"
              >{{ pendingFeeItems.length }}</span
            >
          </button>
        </li>
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'flat-fee' }"
            @click="activeTab = 'flat-fee'"
          >
            Flat-Fee Invoices
          </button>
        </li>
      </ul>

      <LoadingIndicatorComponent v-if="isLoading && activeTab !== 'flat-fee'" />

      <div v-else>
        <!-- Invoice Bundles tab -->
        <div v-if="activeTab === 'bundles'">
          <InvoiceBundlesTable :bundles="bundles" />
        </div>

        <!-- Pending Fee Items tab -->
        <div v-if="activeTab === 'pending-fees'">
          <PendingFeeItemsTable
            :items="pendingFeeItems"
            :company-id="companyId"
            :company-country="selectedCompanyCountry"
            :company-defaults="companyDefaults"
            :recipient-candidates="recipientCandidates"
            @settled="onSettled"
            @refresh="loadData"
          />
        </div>

        <!-- Flat-Fee Invoices tab -->
        <div v-if="activeTab === 'flat-fee'">
          <FlatFeeInvoicesTab :company-id="companyId" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import CompanySearchDropdown from "@/components/common/CompanySearchDropdown.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { InvoiceItem } from "@/types";
import { inject, ref, watch } from "vue";

import FlatFeeInvoicesTab from "@/admin/invoicing/FlatFeeInvoicesTab.vue";
import InvoiceBundlesTable from "@/admin/invoicing/InvoiceBundlesTable.vue";
import PendingFeeItemsTable from "@/admin/invoicing/PendingFeeItemsTable.vue";
import type {
  CompanyDefaults,
  InvoiceBundle,
  RecipientCandidate,
} from "@/admin/invoicing/types";

const props = defineProps<{
  companyId?: number;
}>();

const emit = defineEmits<{
  "update:companyId": [companyId: number | undefined];
}>();

const onCompanyIdUpdate = (value: number | string | null) => {
  const parsed = typeof value === "string" ? parseInt(value, 10) : value;
  emit("update:companyId", parsed ?? undefined);
};

const globalErrorHandler = inject("globalErrorHandler") as (error: any) => void;

const selectedCompanyCountry = ref<string | undefined>();
// The companyId that selectedCompanyCountry was resolved for — guards against
// both the watcher wiping a value the dropdown @select just set, and stale lookups.
let companyIdForSelectedCountry: number | undefined;
const isLoading = ref(false);
const activeTab = ref<"bundles" | "pending-fees" | "flat-fee">("bundles");
const bundles = ref<InvoiceBundle[]>([]);
const pendingFeeItems = ref<InvoiceItem[]>([]);
const companyDefaults = ref<CompanyDefaults | null>(null);
const recipientCandidates = ref<RecipientCandidate[]>([]);
let loadRequestId = 0;

function handleCompanySelect(company: { id: number; country?: string }) {
  // Only cache when the dropdown actually knows the country; otherwise leave
  // the watcher's /api/admin/companies fallback lookup to fill it in.
  if (company.country) {
    selectedCompanyCountry.value = company.country;
    companyIdForSelectedCountry = company.id;
  }
}

async function loadData() {
  if (!props.companyId) {
    bundles.value = [];
    pendingFeeItems.value = [];
    companyDefaults.value = null;
    recipientCandidates.value = [];
    return;
  }
  bundles.value = [];
  pendingFeeItems.value = [];
  companyDefaults.value = null;
  recipientCandidates.value = [];
  isLoading.value = true;
  const currentRequestId = ++loadRequestId;
  try {
    const [bundlesRes, feeItemsRes] = await Promise.all([
      goSolidApi.get(`/api/admin/company/${props.companyId}/invoicing/bundles`),
      goSolidApi.get(
        `/api/admin/company/${props.companyId}/invoicing/pending-fee-items`
      ),
    ]);

    if (currentRequestId !== loadRequestId) return;

    // Assign stable IDs for DataTable dataKey
    bundles.value = (bundlesRes.data.bundles ?? []).map(
      (b: InvoiceBundle, idx: number) => ({
        ...b,
        id: b.fee_invoices[0]?.id ?? b.expense_invoice?.id ?? `bundle-${idx}`,
      })
    );
    pendingFeeItems.value = feeItemsRes.data.invoice_items ?? [];
    companyDefaults.value = feeItemsRes.data.company_defaults ?? null;
    recipientCandidates.value = feeItemsRes.data.recipient_candidates ?? [];
  } catch (error: unknown) {
    if (currentRequestId !== loadRequestId) return;
    globalErrorHandler(error);
  } finally {
    if (currentRequestId === loadRequestId) {
      isLoading.value = false;
    }
  }
}

async function onSettled() {
  activeTab.value = "bundles";
  await loadData();
}

watch(
  () => props.companyId,
  async (companyId) => {
    // Cross-panel navigation (view:company) changes companyId without a
    // dropdown @select; only then is the stored country for another company.
    if (companyId !== companyIdForSelectedCountry) {
      selectedCompanyCountry.value = undefined;
      companyIdForSelectedCountry = undefined;
    }
    if (companyId && !selectedCompanyCountry.value) {
      try {
        const res = await goSolidApi.get("/api/admin/companies");
        // Drop stale responses: the user may have switched company again
        // while this request was in flight.
        if (res.status === 200 && props.companyId === companyId) {
          const match = Object.values(
            res.data as Record<number, { id: number; country?: string }>
          ).find((c) => c.id === companyId);
          if (match?.country) {
            selectedCompanyCountry.value = match.country;
            companyIdForSelectedCountry = companyId;
          }
        }
      } catch {
        // Country lookup failed — description auto-fill degrades to English defaults,
        // deliberately non-blocking for the rest of the panel.
      }
    }
    loadData();
  },
  { immediate: true }
);
</script>
